import React from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { PILLAR_NAME_MAX_LENGTH, emptyRow, validate } from "./pillarsDef";

/**
 * The pillar taxonomy editor inside the Generate Full Report modal.
 *
 * Collapsed by default and headed "optional", because it is: leaving it empty
 * is the old behaviour, and most runs will. Opened, it names the pillars this
 * assessment measures and what each one covers — autoconfig classifies every
 * question against them, and the definitions are what separate two pillars
 * whose names overlap.
 *
 * Applies to whichever input the modal is pointed at, an uploaded JSON file or
 * a selected insight, since the taxonomy is a property of the assessment rather
 * than of how its data arrived.
 */
// `toolbar` renders above the rows — the "start from a project" picker, which
// replaces them wholesale rather than editing one.
const PillarsDefEditor = ({ rows, onChange, disabled = false, toolbar = null }) => {
    const { errors, count } = validate(rows);

    const update = (index, field, value) =>
        onChange(rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));

    const addRow = () => onChange([...rows, emptyRow()]);

    const removeRow = (index) =>
        onChange(rows.length > 1 ? rows.filter((_, i) => i !== index) : [emptyRow()]);

    return (
        <Accordion
            disableGutters
            sx={{ mb: 3, border: "1px solid", borderColor: "divider", "&:before": { display: "none" } }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Custom pillars
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        optional — leave empty to use the file&apos;s own pillars
                    </Typography>
                    {count > 0 && (
                        <Chip
                            size="small"
                            color="primary"
                            label={`${count} pillar${count === 1 ? "" : "s"}`}
                        />
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                {toolbar}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Name the pillars this assessment measures and say what each one covers.
                    Autoconfig reads the definitions to decide which pillar every open-text and
                    Likert question belongs to, so it helps to say what a pillar excludes as well
                    as what it includes.
                </Typography>

                <Stack spacing={1.5}>
                    {rows.map((row, index) => (
                        <Stack key={index} direction="row" spacing={1.5} alignItems="flex-start">
                            <TextField
                                label="Pillar"
                                size="small"
                                value={row.name}
                                onChange={(e) => update(index, "name", e.target.value)}
                                error={Boolean(errors[index])}
                                helperText={errors[index] || " "}
                                disabled={disabled}
                                inputProps={{ maxLength: PILLAR_NAME_MAX_LENGTH }}
                                sx={{ width: 220, flexShrink: 0 }}
                            />
                            <TextField
                                label="What this pillar covers"
                                size="small"
                                value={row.definition}
                                onChange={(e) => update(index, "definition", e.target.value)}
                                disabled={disabled}
                                multiline
                                minRows={2}
                                fullWidth
                                helperText=" "
                            />
                            <Tooltip title="Remove this pillar">
                                <span>
                                    <IconButton
                                        onClick={() => removeRow(index)}
                                        disabled={disabled}
                                        aria-label={`Remove pillar ${row.name || index + 1}`}
                                        size="small"
                                        sx={{ mt: 0.5 }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    ))}
                </Stack>

                <Button startIcon={<AddIcon />} onClick={addRow} disabled={disabled} size="small">
                    Add pillar
                </Button>
            </AccordionDetails>
        </Accordion>
    );
};

export default PillarsDefEditor;
